/**
 * ONE reload budget for the document, shared by every mechanism that can reload
 * it.
 *
 * Two independent recovery mechanisms can decide to reload this document:
 *
 *   - the BOOT WATCHDOG in `index.html`, when an entry asset is dropped before
 *     anything has executed; and
 *   - `lazyWithRetry`, when a code-split route chunk cannot be fetched.
 *
 * They own different stages and both are needed. They must NOT own different
 * budgets: with two uncoordinated budgets of two reloads each, one flaky-network
 * episode costs the owner four document reloads — each one discarding whatever
 * the previous document had already fetched, while the network is still
 * unstable. The budget belongs to the DOCUMENT, so there is exactly one of it,
 * and whichever mechanism spends it first leaves less for the other.
 *
 * TWO BOUNDS, NOT ONE. `MAX_DOCUMENT_RELOADS` bounds a burst and forgives it
 * after `DOCUMENT_RELOAD_WINDOW_MS`, because a fresh incident an hour later
 * deserves its own recovery. `MAX_DOCUMENT_RELOADS_PER_TAB` bounds the tab and
 * never forgives anything, because a fault that does not go away would
 * otherwise collect a fresh burst every minute for ever.
 *
 * THIS BUDGET GOVERNS AUTOMATIC RELOADS ONLY. A reload a PERSON asked for — the
 * Retry button on the watchdog's panel, the reload button on
 * `ChunkLoadErrorBoundary` — is a decision, not a loop, and is deliberately
 * outside these bounds. The watchdog's Retry additionally CLEARS them, because
 * someone asking for another attempt is the one event that is evidence another
 * attempt is wanted.
 *
 * The watchdog cannot import this module: it has to be running already when the
 * entry graph fails, which is before any module executes. It therefore repeats
 * the key names, both caps and the window inline in `index.html`, and
 * `tests/architecture/document-reload-budget.test.ts` fails if the two copies
 * ever disagree.
 */

/** Reloads already spent on the current incident. */
export const DOCUMENT_RELOAD_COUNT_KEY = 'fm-document-reload-count';
/** When the last reload was spent, so an old count can be told from a live one. */
export const DOCUMENT_RELOAD_AT_KEY = 'fm-document-reload-at';
/** Total automatic document reloads allowed per incident, across mechanisms. */
export const MAX_DOCUMENT_RELOADS = 2;
/** Reloads older than this belong to an earlier incident, not the current one. */
export const DOCUMENT_RELOAD_WINDOW_MS = 60_000;

/**
 * Automatic reloads this TAB has spent, over its whole life. NEVER expires and
 * is never cleared by any automatic path.
 *
 * WHY A SECOND COUNTER. The windowed count above governs
 * a BURST: it forgives reloads older than a minute, on the reasoning that they
 * belong to an earlier incident. That reasoning fails against a fault which
 * simply does not go away — a stale-hash deploy served from an `index.html`
 * cache, the bead's own scenario. The route chunk 404s, `lazyWithRetry` burns
 * its (now ~1s of) targeted retries and reloads, the app boots, the router lands on
 * the same route, and the chunk 404s again. Two reloads, then a refusal — and
 * then, sixty seconds later, the window forgives them and the whole thing
 * begins again. The tab reloads itself for as long as the owner leaves it open;
 * a slower loop is still a loop the owner cannot escape.
 *
 * So the window may govern the burst rate. It must not govern the total.
 */
export const DOCUMENT_RELOAD_TOTAL_KEY = 'fm-document-reload-total';

/**
 * The ceiling on AUTOMATIC document reloads for the life of the tab.
 *
 * Sized to leave every legitimate recovery intact and nothing over: the boot
 * watchdog may spend its whole backoff table (MAX_DOCUMENT_RELOADS) bringing a
 * dropped entry asset up, and a genuinely separate route-chunk incident later
 * in the same tab may spend another full budget. Beyond that, a document that
 * has reloaded itself four times is not recovering, and the owner is better
 * served by a surface that says so and offers a manual retry.
 *
 * Only a PERSON clears this — the Retry button in the watchdog's panel. That is
 * the one event which is evidence that another attempt is actually wanted.
 */
export const MAX_DOCUMENT_RELOADS_PER_TAB = 2 * MAX_DOCUMENT_RELOADS;

/**
 * Read a counter, or `null` when it cannot be trusted (storage unavailable,
 * value corrupted or negative). `null` is never coerced to a number here —
 * callers decide what an unreadable budget means, and they all decide "spent".
 */
function readCounter(key: string): number | null {
  try {
    const raw = Number(window.sessionStorage.getItem(key) ?? '0');
    return Number.isFinite(raw) && raw >= 0 ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Automatic document reloads already spent on the current incident.
 *
 * An unreadable budget reports the whole budget as spent. That is not a fallback
 * value standing in for the real one: it is the only end of the range that is
 * safe, because the alternative is an unbounded reload loop on a browser whose
 * sessionStorage does not work. A surface that says the application did not load
 * is always better than a page that reloads forever.
 */
export function spentDocumentReloads(now: number = Date.now()): number {
  const count = readCounter(DOCUMENT_RELOAD_COUNT_KEY);
  const at = readCounter(DOCUMENT_RELOAD_AT_KEY);
  if (count === null || at === null) return MAX_DOCUMENT_RELOADS;
  if (at === 0 || now - at > DOCUMENT_RELOAD_WINDOW_MS) return 0;
  return count;
}

/**
 * Automatic reloads spent over the whole life of this tab. Unreadable storage
 * reports the ceiling, for the same reason `spentDocumentReloads` does: the only
 * safe end of the range is the one that cannot loop.
 */
export function totalDocumentReloads(): number {
  const total = readCounter(DOCUMENT_RELOAD_TOTAL_KEY);
  return total === null ? MAX_DOCUMENT_RELOADS_PER_TAB : total;
}

/**
 * Claim one document reload. Returns false when either bound is reached — the
 * per-incident burst or the tab's absolute total — or when the claim cannot be
 * written. In every case the caller must surface the failure instead of
 * reloading.
 */
export function consumeDocumentReload(now: number = Date.now()): boolean {
  const total = totalDocumentReloads();
  if (total >= MAX_DOCUMENT_RELOADS_PER_TAB) return false;
  const spent = spentDocumentReloads(now);
  if (spent >= MAX_DOCUMENT_RELOADS) return false;
  try {
    window.sessionStorage.setItem(DOCUMENT_RELOAD_COUNT_KEY, String(spent + 1));
    window.sessionStorage.setItem(DOCUMENT_RELOAD_AT_KEY, String(now));
    window.sessionStorage.setItem(DOCUMENT_RELOAD_TOTAL_KEY, String(total + 1));
    return true;
  } catch {
    return false;
  }
}
